import { stateMark } from './diary-colours'
import { visitColour } from './day-state'
import { swallowNextTap, trackDueDrag } from './due-bar'
import { buzz } from './haptic'
import { dayLabel, el, todayISO } from './ui'
import { currencySymbol } from './round-config'
import type { Clean } from './types'
import './diary-week-phone.css'

const reduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Presentation only. Both ways of choosing a day call the supplied existing placement action.
export function weekName(name: string, key: string, pick: () => void, place: (date: string) => void): HTMLButtonElement {
  const b = el('button', { type: 'button', class: 'diary-week-name', 'data-week-pick': key,
    'aria-label': `Pick ${name}, then tap a day. Or hold and drag onto a day.` }, name) as HTMLButtonElement
  b.addEventListener('click', (e) => { e.stopPropagation(); pick() })
  weekNameDrag(b, name, place)
  return b
}

// Liam, 3 Nov 2026 ("It wont let me drag onto a day"): the whole card can be held, not only the name (skip names the
// parts of it that are buttons of their own); and on an iPhone the drag follows the finger's touch, so the tray starting
// to scroll under it does not drop the name.
export function weekNameDrag(b: HTMLElement, name: string, place: (date: string) => void, skip = ''): void {
  let touching = false
  let press: { id: number; x: number; y: number } | null = null, timer = 0
  let ghost: HTMLElement | null = null, over: HTMLElement | null = null, release: (() => void) | null = null
  let lastX = 0, lastY = 0, frame = 0
  const targets = (): HTMLElement[] => [...document.querySelectorAll<HTMLElement>('#screen.diary-phone-week-view .diary-week-strip button[data-date]')]
  const clearPress = (): void => { window.clearTimeout(timer); timer = 0; press = null }
  const aim = (x: number, y: number): HTMLElement | null => {
    const tile = document.elementFromPoint(x, y)?.closest<HTMLElement>('.diary-week-strip button[data-date]')
    return tile && (tile.dataset.date ?? '') >= todayISO() ? tile : null
  }
  const follow = (x: number, y: number): void => {
    if (!ghost) return
    lastX = x; lastY = y
    // The name rides above the thumb. The tile under the thumb is always the target.
    const left = Math.max(8, Math.min(x - 90, window.innerWidth - 188))
    ghost.style.left = `${left}px`; ghost.style.top = `${y - 72}px`
    const tile = aim(x, y)
    if (tile === over) return
    over?.classList.remove('diary-week-drop-over'); over = tile; over?.classList.add('diary-week-drop-over')
  }
  const scroll = (): void => {
    if (!ghost) return
    if (!b.isConnected) { finish(); return }
    const top = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0
    if (lastY < top + 32) window.scrollBy(0, -8)
    else if (lastY > window.innerHeight - 80) window.scrollBy(0, 8)
    follow(lastX, lastY); frame = requestAnimationFrame(scroll)
  }
  const finish = (): void => {
    clearPress(); cancelAnimationFrame(frame)
    ghost?.remove(); ghost = null; over?.classList.remove('diary-week-drop-over'); over = null
    for (const t of targets()) t.classList.remove('diary-week-drop-zone')
    b.classList.remove('is-lifted'); document.body.classList.remove('diary-week-dragging', 'diary-due-dragging')
    window.removeEventListener('pointermove', move, true); window.removeEventListener('pointerup', up, true)
    window.removeEventListener('pointercancel', pointerGone, true); window.removeEventListener('hashchange', cancel)
    window.removeEventListener('pointerdown', second, true); window.removeEventListener('keydown', escape, true)
    const ended = release; release = null; ended?.()
  }
  const cancel = (): void => { if (ghost) swallowNextTap(); finish() }
  const pointerGone = (): void => { if (ghost && touching) return; cancel() }
  const second = (e: PointerEvent): void => { if (press && e.pointerId !== press.id) cancel() }
  const escape = (e: KeyboardEvent): void => { if (e.key === 'Escape' && ghost) { e.preventDefault(); e.stopImmediatePropagation(); cancel() } }
  const move = (e: PointerEvent): void => {
    if (!press || e.pointerId !== press.id) return
    if (!ghost) { if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > 10) finish(); return }
    e.preventDefault(); follow(e.clientX, e.clientY)
  }
  const up = (e: PointerEvent): void => {
    if (!press || e.pointerId !== press.id) return
    if (!ghost) { finish(); return }
    const date = aim(e.clientX, e.clientY)?.dataset.date
    swallowNextTap(); finish()
    if (date) { buzz('light'); place(date) }
  }
  b.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || press) return
    if (skip && (e.target as Element | null)?.closest(skip)) return
    press = { id: e.pointerId, x: e.clientX, y: e.clientY }
    window.addEventListener('pointermove', move, true); window.addEventListener('pointerup', up, true)
    window.addEventListener('pointercancel', pointerGone, true); window.addEventListener('hashchange', cancel)
    window.addEventListener('pointerdown', second, true); window.addEventListener('keydown', escape, true)
    timer = window.setTimeout(() => {
      if (!press || !b.isConnected) { finish(); return }
      release = trackDueDrag(b, cancel)
      ghost = el('div', { class: `diary-week-ghost${reduced() ? ' is-still' : ''}`, 'aria-hidden': 'true' }, name)
      document.body.append(ghost); b.classList.add('is-lifted')
      document.body.classList.add('diary-week-dragging', 'diary-due-dragging')
      for (const t of targets()) if ((t.dataset.date ?? '') >= todayISO()) t.classList.add('diary-week-drop-zone')
      // Shrink before aiming so even a name in the large tray can reach the strip.
      document.querySelector<HTMLElement>('.diary-week-tray [data-week-resize-small]')?.click()
      follow(press.x, press.y); frame = requestAnimationFrame(scroll)
    }, 260)
  })
  b.addEventListener('touchstart', () => { touching = true }, { passive: true })
  b.addEventListener('touchmove', (e) => {
    if (e.touches.length > 1) { cancel(); return }
    if (!ghost) return
    e.preventDefault(); const t = e.touches[0]; if (t) follow(t.clientX, t.clientY)
  }, { passive: false })
  const touchEnd = (e: TouchEvent): void => {
    touching = false
    if (!ghost) return
    const t = e.changedTouches[0], date = t ? aim(t.clientX, t.clientY)?.dataset.date : undefined
    swallowNextTap(); finish()
    if (date) { buzz('light'); place(date) }
  }
  b.addEventListener('touchend', touchEnd)
  b.addEventListener('touchcancel', () => { touching = false; if (ghost) cancel() })
  b.addEventListener('contextmenu', (e) => e.preventDefault())
  b.addEventListener('dragstart', (e) => e.preventDefault())
}

export interface WeekDayRow {
  iso: string; visits: Clean[]; money: string; needs: string; workers: string; support: HTMLElement[]
}
export function phoneWeekRows(rows: WeekDayRow[], o: {
  open: (iso: string) => void
  // A tap on a name opens that visit's card (design Q, Liam 4 Nov 2026); without it, the day as before.
  visit?: (cl: Clean, chip: HTMLElement) => void
  pick: (cl: Clean) => void
  place: (cl: Clean, iso: string) => void
  movable: (cl: Clean) => boolean
  timePending: (cl: Clean) => boolean
  time: (cl: Clean) => string
  who: (cl: Clean) => string
  detail: (cl: Clean) => string
  payment: (cl: Clean) => string
  filtered: boolean
}): HTMLElement {
  const list = el('div', { class: 'diary-phone-week', role: 'group', 'aria-label': 'This week, day by day' })
  for (const { iso, visits, money, needs, workers, support } of rows) {
    if (!visits.length && !support.length) continue
    const day = el('button', { type: 'button', class: 'diary-pw-day', 'aria-label': `Open ${dayLabel(iso)} in Day` },
      el('b', {}, `${new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' })} ${Number(iso.slice(8))}`))
    day.addEventListener('click', () => o.open(iso))
    const chips = el('div', { class: 'diary-pw-names diary-pw-pills' })
    // Design 1B (Liam, 3 Nov 2026: "1B is better", then "yep thats better"; https://claude.ai/artifact/1yQ4j1qCSnSs2QCPVnKC1m):
    // the names run across the whole card, eight before "+ more".
    const SHOWN = 8
    for (const cl of visits.slice(0, SHOWN)) {
      const fullName = cl.customer_name ?? 'Visit', look = visitColour(cl), canMove = o.movable(cl)
      // Row chips open Day on tap. Eligible holds keep the same existing move path;
      // Day's Move and the tray's tap-then-day path remain keyboard accessible.
      const tap = (): void => { if (o.visit) o.visit(cl, chip); else o.open(iso) }
      const chip: HTMLElement = canMove ? weekName(fullName, `visit:${cl.id}`, tap, date => o.place(cl, date)) :
        el('button', { type: 'button' })
      if (!canMove) chip.addEventListener('click', tap)
      chip.setAttribute('data-week-visit', String(cl.id))
      chip.classList.add('diary-pw-pill', `is-${look ?? 'check'}`)
      const booked = cl.state === 'booked', pending = booked && o.timePending(cl)
      const when = booked ? o.time(cl).split(' · ')[0] : ''
      if (pending) chip.classList.add('is-time')
      const facts = booked ? [o.detail(cl), when, pending ? 'time to confirm' : '', o.who(cl)].filter(Boolean).join(' · ') : ''
      chip.setAttribute('aria-label', `${fullName}, ${look === 'done' ? `done, ${o.payment(cl)}` : look === 'agreed' ? 'day agreed' : 'not agreed'}${facts ? `, ${facts}` : ''}. ${o.visit ? 'Open the visit' : 'Open the day'}.${canMove ? ' Or hold and drag to move; Move is also in Day.' : ''}`)
      chip.title = chip.getAttribute('aria-label') ?? fullName
      const face = el('span', {class:'diary-pw-chip-face'}, ...(look ? [stateMark(look)] : []),
        el('span', {class:'diary-pw-name'}, fullName.trim().split(/\s+/)[0] || 'Visit'),
        ...(when ? [el('span', {class:'diary-pw-time'}, when)] : []))
      if (look === 'done' && cl.payment_status === 'unpaid') face.append(el('span', {class:'diary-week-payment', 'aria-hidden':'true'}, currencySymbol()))
      chip.replaceChildren(face)
      chips.append(chip)
    }
    if (visits.length > SHOWN) {
      const more = el('button', { type: 'button', class: 'diary-pw-pill is-more', 'aria-label': `Open ${dayLabel(iso)}, ${visits.length - SHOWN} more visits` }, el('span', {class:'diary-pw-chip-face'}, `+${visits.length - SHOWN} more`))
      more.addEventListener('click', () => o.open(iso)); chips.append(more)
    }
    // The top line: the day, who is out, the money; the names under it; anything still to check below them.
    const head = el('div', { class: 'diary-pw-head' }, day, ...(workers ? [el('span', { class: 'diary-pw-workers' }, workers)] : []), el('span', { class: 'diary-pw-total' }, money))
    const row = el('section', { class: 'diary-phone-week-row is-1b', 'data-date': iso, 'aria-label': `${dayLabel(iso)}, ${visits.length} visits` }, head,
      el('div', { class: 'diary-pw-mid' }, chips, ...(needs ? [el('p', { class: 'diary-pw-confirm' }, needs)] : [])))
    if (support.length) row.append(el('div', { class: 'diary-pw-support' }, ...support))
    row.addEventListener('click', e => {
      if (!(e.target as Element).closest?.('button, a, input, select, textarea, summary, details')) o.open(iso)
    })
    list.append(row)
  }
  if (!list.children.length) list.append(el('p', { class: 'diary-week-empty' }, o.filtered ? 'No matching visits. Clear filters to see everyone.' : 'Nothing booked this week. Tap a day or Book a visit.'))
  return list
}
